<script setup lang="ts">
import { computed } from "vue"

import type { Option } from "@/types/common.type"
import Multiselect from "@vueform/multiselect"

// const randomPrefix = Math.random().toString(36).slice(2, 10)

const props = defineProps({
  options: { type: Array<Option>, required: true },
  field: { type: String, required: true },
  modelValue: { type: [String, Number, Boolean, Array, Object, null], required: true },
  label: { type: String, default: "" },
  searchable: { type: Boolean, default: false },
  multiple: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  suppressRequiredMark: { type: Boolean, default: false },
})
const emits = defineEmits(["update:modelValue"])

// proxy for v-model binding
const internalValue = computed({
  get: () => props.modelValue,
  set: (v) => emits("update:modelValue", v),
})
</script>

<template>
  <div class="input-box">
    <label v-if="label" class="form-label" :for="field">
      {{ label }} <span class="text-danger" v-if="required && !suppressRequiredMark">*</span>
    </label>

    <!-- Simple native select for non-searchable, single selection -->
    <select
      v-if="!searchable && !multiple"
      v-model="internalValue"
      class="form-select"
      :id="field"
      :name="field"
      :required="required"
      :disabled="disabled"
    >
      <option></option>
      <option v-for="option in options" :key="option.value" :value="option.value">{{ option.label }}</option>
    </select>

    <!-- vueform/multiselect for searchable or multiple selection -->
    <Multiselect
      v-else
      v-model="internalValue"
      class="multiselect-custom"
      :options="options"
      :searchable="searchable"
      :mode="multiple ? 'tags' : 'single'"
      :required="required"
      :disabled="disabled"
    />
  </div>
</template>

<style scoped>
.form-label {
  font-weight: 600;
}

.multiselect-custom {
  --ms-line-height: 1.25;
  --ms-border-color: #dee2e6;
  --ms-border-color-active: #86b7fe;
  --ms-radius: 0.375rem;
  --ms-tag-bg: #212529;
  --ms-option-bg-selected: #212529;
  --ms-option-bg-selected-pointed: #515559;
  --ms-max-height: 20rem;
}
</style>

<style src="@vueform/multiselect/themes/default.css"></style>
